import { makeToast as beaconMakeToast } from "@braze/beacon-core";

import { logFullstoryEvent } from "@lib/logFullstoryEvent";
// Deep paths, not the `@lib/scraping` barrel: the barrel imports this package back.
import { findToastElement } from "@lib/scraping/findToastElement";
import { readToastText } from "@lib/scraping/readToastText";

import { brazeToasterId } from "./constants";
import { recordToast } from "./recentToasts";
import type { ToastContent, ToastIntent, ToastOptions, ToastReturn } from "./types";

/**
 * Send a toast message
 *
 * See https://beacon-storybook.braze.com/?path=/docs/core-alerts-toaster--basic-use#maketoast for full documentation
 * @param intent one of `"danger"`, `"info"`, `"success"`, or `"warning"`
 * @param content a string, or a React component
 * @param options makeToast options
 * @returns
 */
export const makeToast = (
  intent: ToastIntent,
  content: ToastContent,
  { containerId = brazeToasterId, onOpen, ...options }: ToastOptions = {}
) => {
  if (intent === "danger" || intent === "warning") {
    logFullstoryEvent("toast", { intent, content });
  }
  // Assigned before the Toaster can mount the toast: `onOpen` is a mount effect and nothing
  // opens in the same tick as this call. Declared up front all the same, so a react-toastify
  // that ever opened synchronously would skip one recording rather than throw.
  let toastId: ToastReturn = null;
  toastId = beaconMakeToast(intent, content, {
    containerId,
    ...options,
    onOpen: (props) => {
      onOpen?.(props);
      // Record what the toast says for the BrazeAI Operator page scraper, which otherwise loses
      // an auto-closing notification after ~10s. Reading the rendered toast makes a React-node
      // body no more opaque than a string one, and leaves callers nothing to wire up. It is a
      // snapshot taken at open: a body that fills in or counts down later records as it first
      // appeared. Recording here rather than at the call is what keeps a toast the Toaster never
      // showed out of the buffer — see `findToastElement`. (`toast.onChange`'s "added" fires
      // before React renders, so it is not an option when the deprecated hooks eventually go.)
      const toast = toastId === null ? undefined : findToastElement(containerId, toastId);
      if (toast) {
        // Read with PII withheld: this entry outlives the request that reads it, so it cannot
        // know whether that reader is a workspace the live scrape trusts with the content.
        recordToast(intent, readToastText(toast, true));
      }
    },
  });
  return toastId;
};
